/**
 * Event Stats Module - Dashboard 統計與計算
 */

import { supabase } from '../core/supabaseClient'
import type {
  EventRow,
  EventDashboardResponse,
  EventServiceContext
} from './types'

// ============ Pure Functions ============

export function groupBy(events: EventRow[], field: keyof EventRow): Record<string, number> {
  const result: Record<string, number> = {}
  events.forEach(e => {
    const key = String(e[field] || 'unknown')
    result[key] = (result[key] || 0) + 1
  })
  return result
}

export function calculateTrend(events: EventRow[], startDate: Date, endDate: Date): Array<{ date: string; total: number; resolved: number }> {
  const trend: Array<{ date: string; total: number; resolved: number }> = []
  const current = new Date(startDate)
  current.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(23, 59, 59, 999)

  while (current <= end) {
    const dayStart = new Date(current)
    const dayEnd = new Date(current)
    dayEnd.setHours(23, 59, 59, 999)

    const dayEvents = events.filter(e => {
      const created = new Date(e.created_at)
      return created >= dayStart && created <= dayEnd
    })
    const dayResolved = events.filter(e => {
      if (!e.resolved_at) return false
      const resolved = new Date(e.resolved_at)
      return resolved >= dayStart && resolved <= dayEnd
    })

    trend.push({
      date: current.toISOString().split('T')[0],
      total: dayEvents.length,
      resolved: dayResolved.length
    })

    current.setDate(current.getDate() + 1)
  }
  return trend
}

export function calculateFieldRanking(events: EventRow[], limit = 8): Array<{ id: string; name: string; count: number; unresolved: number }> {
  const fieldCounts: Record<string, { id: string; name: string; count: number; unresolved: number }> = {}
  events.forEach(e => {
    const fieldId = e.field_id || ''
    const fieldName = e.field_name || '未知案場'
    if (!fieldCounts[fieldId]) {
      fieldCounts[fieldId] = { id: fieldId, name: fieldName, count: 0, unresolved: 0 }
    }
    fieldCounts[fieldId].count++
    if (!e.is_resolved) {
      fieldCounts[fieldId].unresolved++
    }
  })

  return Object.values(fieldCounts)
    .filter(f => f.id && f.name !== '未知案場')
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

export function calculateFlowData(events: EventRow[]): Record<string, number> {
  const flow: Record<string, number> = {
    alarm_high: 0, alarm_medium: 0, alarm_low: 0,
    warning_high: 0, warning_medium: 0, warning_low: 0,
    risk_high: 0, risk_medium: 0, risk_low: 0,
    incident_high: 0, incident_medium: 0, incident_low: 0,
    info_high: 0, info_medium: 0, info_low: 0,
    condition_high: 0, condition_medium: 0, condition_low: 0,
    system_high: 0, system_medium: 0, system_low: 0,
    high_resolved: 0, high_pending: 0,
    medium_resolved: 0, medium_pending: 0,
    low_resolved: 0, low_pending: 0
  }

  events.forEach(e => {
    const type = e.type || 'system'
    const priority = e.priority || 'low'
    const isResolved = e.is_resolved

    const normalizedType = (type === 'maintenance' || type === 'timeout' || type === 'recovery' || type === 'temperature') ? 'system' : type

    const typeKey = `${normalizedType}_${priority}`
    if (flow[typeKey] !== undefined) {
      flow[typeKey]++
    }

    const resultKey = `${priority}_${isResolved ? 'resolved' : 'pending'}`
    if (flow[resultKey] !== undefined) {
      flow[resultKey]++
    }
  })

  return flow
}

// ============ RPC Functions (Pure, no ctx needed) ============

export async function getEventStats(
  startDate: Date | string,
  endDate: Date | string,
  fieldIds: string[] | null = null,
  organizationId: string | null = null
) {
  const { data, error } = await supabase.rpc('get_event_stats', {
    p_start_date: new Date(startDate).toISOString(),
    p_end_date: new Date(endDate).toISOString(),
    p_field_ids: fieldIds,
    p_organization_id: organizationId
  })

  if (error) throw error
  return data
}

export async function getEventTrend(
  startDate: Date | string,
  endDate: Date | string,
  fieldIds: string[] | null = null,
  organizationId: string | null = null
) {
  const { data, error } = await supabase.rpc('get_event_trend', {
    p_start_date: new Date(startDate).toISOString(),
    p_end_date: new Date(endDate).toISOString(),
    p_field_ids: fieldIds,
    p_organization_id: organizationId
  })

  if (error) throw error
  return data
}

export async function getFieldEventRanking(
  startDate: Date | string,
  endDate: Date | string,
  fieldIds: string[] | null = null,
  organizationId: string | null = null,
  limit = 8
) {
  const { data, error } = await supabase.rpc('get_field_event_ranking', {
    p_start_date: new Date(startDate).toISOString(),
    p_end_date: new Date(endDate).toISOString(),
    p_field_ids: fieldIds,
    p_organization_id: organizationId,
    p_limit: limit
  })

  if (error) throw error
  return data
}

// ============ Context-Aware Functions ============

export async function getTodayEventStats(
  ctx: EventServiceContext,
  fieldIds: string[] | null = null,
  organizationId: string | null = null,
  userId: string | null = null
) {
  const { data, error } = await supabase.rpc('get_today_event_stats', {
    p_field_ids: fieldIds,
    p_organization_id: organizationId,
    p_user_id: userId || ctx.currentUserId
  })

  if (error) throw error
  return data
}

export async function getDashboardStats(
  ctx: EventServiceContext,
  days = 7,
  fieldIds: string[] | null = null,
  organizationId: string | null = null,
  minDurationMinutes = 0
): Promise<EventDashboardResponse> {
  try {
    const { data, error } = await supabase.rpc('get_events_dashboard_stats', {
      p_days: days,
      p_min_duration_minutes: minDurationMinutes,
      p_field_ids: fieldIds,
      p_organization_id: organizationId
    })

    if (error) throw error

    return data as EventDashboardResponse
  } catch (error) {
    console.error('[EventService] getDashboardStats 失敗:', error)
    console.warn('[EventService] 嘗試使用前端計算作為 fallback...')
    return getDashboardStatsWithDurationFilter(
      ctx,
      new Date(Date.now() - days * 24 * 60 * 60 * 1000),
      new Date(),
      fieldIds,
      organizationId,
      minDurationMinutes,
      days
    )
  }
}

export async function getDashboardStatsWithDurationFilter(
  ctx: EventServiceContext,
  startDate: Date,
  endDate: Date,
  fieldIds: string[] | null,
  organizationId: string | null,
  minDurationMinutes: number,
  days: number
): Promise<EventDashboardResponse> {
  try {
    let queryBuilder = ctx.query()
      .dateRange(startDate, endDate)

    if (fieldIds && fieldIds.length > 0) {
      queryBuilder = queryBuilder.fields(fieldIds)
    }
    if (organizationId) {
      queryBuilder = queryBuilder.organization(organizationId)
    }

    const allEvents = await queryBuilder.execute()

    const minMinutes = minDurationMinutes
    const events = (allEvents || []).filter(event => {
      if (event.is_resolved) {
        return (event.duration_minutes ?? 9999) >= minMinutes
      }
      if (event.created_at) {
        const durationMs = Date.now() - new Date(event.created_at).getTime()
        return durationMs >= minMinutes * 60 * 1000
      }
      return true
    })

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayEvents = events.filter(e => new Date(e.created_at) >= today)
    const todayResolved = events.filter(e => e.resolved_at && new Date(e.resolved_at) >= today)

    const flow_data = calculateFlowData(events)

    const stats = {
      total: events.length,
      resolved: events.filter(e => e.is_resolved).length,
      unresolved: events.filter(e => !e.is_resolved).length,
      high_priority: events.filter(e => e.priority === 'high').length,
      by_type: groupBy(events, 'type'),
      by_priority: groupBy(events, 'priority'),
      flow_data
    }

    const trend = calculateTrend(events, startDate, endDate)
    const ranking = calculateFieldRanking(events, 8)

    const todayStats = {
      new_count: todayEvents.length,
      resolved_count: todayResolved.length
    }

    return {
      stats,
      trend,
      ranking,
      todayStats,
      period: days
    }
  } catch (error) {
    console.error('[EventService] getDashboardStatsWithDurationFilter 失敗:', error)
    throw error
  }
}
